import { Api } from '@axiom/ui';
import type {
  Candidate,
  CandidateCompensation,
  MfaPhoneNumberPreferences,
} from '@axiom/validation';

import { dataUriToBlob } from '../utils/data-uri-util';
import { EnvUtil } from '../utils/env-util';

class CandidateApiClass extends Api {
  constructor() {
    super({
      domain: EnvUtil.apiPortalUrl,
      cookieDomain: EnvUtil.cookieDomain,
    });
  }

  readCandidate(candidateId: string) {
    return super.read<{ data: Candidate }>({
      endpoint: `/candidates/${candidateId}`,
      method: 'GET',
    });
  }

  async updateCandidate(candidateId: string, candidate: Partial<Candidate>) {
    return super.write<{ data: Candidate }>({
      endpoint: `/candidates/${candidateId}`,
      method: 'PATCH',
      body: candidate,
    });
  }

  readCandidateMfaPhoneNumber(candidateId: Candidate['id']) {
    return super.read<{ data: MfaPhoneNumberPreferences }>({
      endpoint: `/candidates/${candidateId}/phoneNumberPreferences`,
      method: 'GET',
    });
  }

  async deleteNotificationsPhoneNumber(candidateId: Candidate['id']) {
    return super.write<{ data: null }>({
      endpoint: `/candidates/${candidateId}/notificationsPhoneNumber`,
      method: 'DELETE',
    });
  }

  async updateRegisteredForMfa(
    candidateId: Candidate['id'],
    body: { registeredForMfa: boolean }
  ) {
    return super.write<{ data: null }>({
      endpoint: `/candidates/${candidateId}/registeredForMfa`,
      method: 'PATCH',
      body,
    });
  }

  readCandidateCompensation(candidateId: Candidate['id']) {
    return super.read<{ data: CandidateCompensation }>({
      endpoint: `/candidates/${candidateId}/compensation`,
      method: 'GET',
    });
  }

  async refreshCandidate(candidateId: string) {
    return super.invalidate(`/candidates/${candidateId}`);
  }

  async refreshCandidateMfaPhoneNumber(candidateId: string) {
    return super.invalidate(
      `/candidates/${candidateId}/phoneNumberPreferences`
    );
  }

  readProfileImageUrl(candidateId: string) {
    return `${this.domain}/candidates/${candidateId}/profileImage`;
  }

  async updateProfileImage(candidateId: string, imageUri: string | Blob) {
    const blob =
      typeof imageUri === 'string' ? dataUriToBlob(imageUri) : imageUri;
    const formData = new FormData();
    formData.append('document', blob, 'document');
    return super.write<{
      data: { profileImageKey: string; profileImageName: string };
    }>({
      endpoint: `/candidates/${candidateId}/profileImage`,
      method: 'PUT',
      body: formData,
    });
  }

  async deleteProfileImage(candidateId: string) {
    return super.write<{
      data: {
        profileImageInfo: {
          profileImageKey: null;
          profileImageName: null;
        };
      };
    }>({
      endpoint: `/candidates/${candidateId}/profileImage`,
      method: 'DELETE',
    });
  }
}

export const CandidateApi = new CandidateApiClass();

export const getCandidate = async (candidateId: string) => {
  const response = await CandidateApi.write<{ data: Candidate }>({
    endpoint: `/candidates/${candidateId}`,
    method: 'GET',
  });
  return response.data;
};
